import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import AccountShell from "@/components/vangard/AccountShell";
import { RequestThread, ThreadToggle } from "@/components/vangard/RequestThread";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  pending: "border-white/25 text-neutral-200",
  confirmed: "border-white/60 text-white",
  completed: "border-white/15 text-neutral-400",
  cancelled: "border-white/10 text-neutral-600",
};

export default function AccountRequestsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/bookings`, { withCredentials: true })
      .then(({ data }) => {
        setRows(data || []);
        if (data?.length) setOpenId(data[0].booking_id);
      })
      .catch(() => setRows([]));
  }, []);

  return (
    <AccountShell active="/account/concierge" title={t("vc.acct.nav.requests")}>
      <div data-testid="account-requests-page">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-neutral-400 font-light max-w-lg">{t("vc.acct.req.sub")}</p>
          <Link
            to="/account/services"
            className="shrink-0 inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
            data-testid="account-new-request"
          >
            {t("vc.acct.req.new")}
          </Link>
        </div>

        {rows === null ? (
          <div className="mt-10 flex items-center gap-3 text-neutral-500 text-sm">
            <Loader2 size={16} className="animate-spin" /> {t("common.loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-7" data-testid="account-requests-empty">
            <div className="text-base">{t("vc.acct.req.empty-title")}</div>
            <p className="text-sm text-neutral-500 font-light mt-2 max-w-md leading-relaxed">{t("vc.acct.req.empty-body")}</p>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 divide-y divide-white/[0.06]" data-testid="account-requests-list">
            {rows.map((r) => {
              const open = openId === r.booking_id;
              return (
                <div key={r.booking_id} className="p-5" data-testid={`request-row-${r.booking_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
                    <div className="min-w-0">
                      <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">
                        {new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-base mt-1.5 capitalize">{(r.service_type || "").replace(/[-_]/g, " ")}</div>
                      {r.preferred_date && (
                        <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-1.5">
                          {t("vc.acct.req.preferred")}: {r.preferred_date}
                        </div>
                      )}
                      <div className="mt-2.5">
                        <ThreadToggle
                          open={open}
                          onClick={() => setOpenId(open ? null : r.booking_id)}
                          testid={`thread-toggle-${r.booking_id}`}
                          label={open ? t("vc.acct.thread.hide") : t("vc.acct.thread.open")}
                        />
                      </div>
                    </div>
                    <span className={`shrink-0 self-start inline-flex items-center rounded-full border px-3.5 py-1 text-[9px] tracking-[0.25em] uppercase ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                      {r.status}
                    </span>
                  </div>

                  {open && <RequestThread booking={r} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AccountShell>
  );
}
